/**
 * One error shape, used by the server to produce failures and by the client to
 * interpret them.
 *
 * The old server returned `{ error: err.message }` roughly forty times, leaking
 * Mongo errors and internal paths, and the client had five separate fetch helpers
 * that each guessed at the response shape. Two of them never checked
 * `response.ok` at all, so a 500 read as a success and a green "added" toast fired
 * over a write that never happened.
 *
 * Audit references prevented here:
 *   server.js #9    (internal error text returned to the client)
 *   store.js #13    (failures indistinguishable from each other)
 *   calendar.jsx #1 (no response.ok check, success toast regardless)
 */

/**
 * Machine-readable failure codes.
 *
 * The client switches on these, never on a message string. Adding a code is a
 * deliberate act; there is no free-text fallback that quietly becomes an API.
 */
export const API_ERROR_CODES = Object.freeze({
  /** Request body, query or params failed the shared schema. 400. */
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  /** No valid session. 401. */
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  /** Authenticated, but not allowed to touch this record. 403. */
  FORBIDDEN: 'FORBIDDEN',
  /** Missing CSRF token, or it did not match the cookie. 403. */
  CSRF_FAILED: 'CSRF_FAILED',
  /** No such record, or not owned by the caller. 404. */
  NOT_FOUND: 'NOT_FOUND',
  /** Version mismatch. The current document is returned alongside. 409. */
  VERSION_CONFLICT: 'VERSION_CONFLICT',
  /** A unique index rejected the write. 409. */
  ALREADY_EXISTS: 'ALREADY_EXISTS',
  /** Rate limited, or locked out. 429. */
  TOO_MANY_REQUESTS: 'TOO_MANY_REQUESTS',
  /** Anything unplanned. 500. The message is always generic. */
  INTERNAL: 'INTERNAL',
  /** A third-party service failed. 502. */
  UPSTREAM_FAILED: 'UPSTREAM_FAILED',
  /** The request was aborted or timed out client-side. Never sent by the server. */
  NETWORK: 'NETWORK',
});

/** HTTP status for each code, in one place so a route cannot pick its own. */
export const STATUS_BY_ERROR_CODE = Object.freeze({
  [API_ERROR_CODES.VALIDATION_FAILED]: 400,
  [API_ERROR_CODES.UNAUTHENTICATED]: 401,
  [API_ERROR_CODES.FORBIDDEN]: 403,
  [API_ERROR_CODES.CSRF_FAILED]: 403,
  [API_ERROR_CODES.NOT_FOUND]: 404,
  [API_ERROR_CODES.VERSION_CONFLICT]: 409,
  [API_ERROR_CODES.ALREADY_EXISTS]: 409,
  [API_ERROR_CODES.TOO_MANY_REQUESTS]: 429,
  [API_ERROR_CODES.INTERNAL]: 500,
  [API_ERROR_CODES.UPSTREAM_FAILED]: 502,
  [API_ERROR_CODES.NETWORK]: 0,
});

/**
 * User-facing text per code.
 *
 * Written once, here, so no route invents its own wording and no internal detail
 * is ever the thing the user reads.
 */
export const DEFAULT_MESSAGE_BY_ERROR_CODE = Object.freeze({
  [API_ERROR_CODES.VALIDATION_FAILED]: 'Some of the information sent was not valid.',
  [API_ERROR_CODES.UNAUTHENTICATED]: 'Please sign in to continue.',
  [API_ERROR_CODES.FORBIDDEN]: 'You do not have access to that.',
  [API_ERROR_CODES.CSRF_FAILED]: 'That request could not be verified. Please reload and try again.',
  [API_ERROR_CODES.NOT_FOUND]: 'That could not be found.',
  [API_ERROR_CODES.VERSION_CONFLICT]:
    'Someone else changed this while you were editing. Their version is shown so you can decide what to keep.',
  [API_ERROR_CODES.ALREADY_EXISTS]: 'That already exists.',
  [API_ERROR_CODES.TOO_MANY_REQUESTS]: 'Too many attempts. Please wait and try again.',
  [API_ERROR_CODES.INTERNAL]: 'Something went wrong on our side.',
  [API_ERROR_CODES.UPSTREAM_FAILED]: 'A connected service is not responding right now.',
  [API_ERROR_CODES.NETWORK]: 'Could not reach the server. Check your connection.',
});

/**
 * A failure that is safe to show a user.
 *
 * Anything thrown that is not an ApiError is treated as unplanned by the error
 * handler: logged in full, reported to the client as a bare INTERNAL with a
 * correlation id and nothing else.
 */
export class ApiError extends Error {
  /**
   * @param {string} code One of {@link API_ERROR_CODES}.
   * @param {object} [options]
   * @param {string} [options.message]  Overrides the default text for this code.
   * @param {object} [options.details]  Safe to show. Field errors, or the current
   *   document on a version conflict. Never internal state.
   * @param {string} [options.correlationId]
   * @param {unknown} [options.cause]   Logged, never sent.
   */
  constructor(code, options = {}) {
    const resolvedCode = API_ERROR_CODES[code] ?? code;
    const message = options.message ?? DEFAULT_MESSAGE_BY_ERROR_CODE[resolvedCode];
    super(message ?? 'Request failed');
    this.name = 'ApiError';
    this.code = resolvedCode;
    this.status = STATUS_BY_ERROR_CODE[resolvedCode] ?? 500;
    this.details = options.details ?? null;
    this.correlationId = options.correlationId ?? null;
    if (options.cause !== undefined) this.cause = options.cause;
  }

  /**
   * Whether retrying the identical request could plausibly succeed.
   *
   * Used by the transport to decide whether to back off and try again. A
   * validation failure or a conflict never becomes correct by repetition.
   *
   * @returns {boolean}
   */
  get isRetryable() {
    return this.status === 429 || this.status >= 500 || this.code === API_ERROR_CODES.NETWORK;
  }
}

/**
 * Whether a value is an ApiError.
 *
 * Checks the shape rather than the prototype, so an error that crossed a module
 * or bundle boundary is still recognised.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isApiError(value) {
  return Boolean(value) && value.name === 'ApiError' && typeof value.code === 'string';
}
